import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CardRecord, CardHistoryEntry, defaultDb, CardType, DiffRange } from '../db/indexedDb';
import { TopicDictationResult, SentenceScore } from '../writing/topicDictation/types';
import { ListeningResult, ListeningSentenceResult, ListeningClozeResult } from '../listening/types';
import { LocalParagraphScore, ParagraphLLMFeedback } from '../db/indexedDb';
import { buildSpeakingPhraseCardId, createSpeakingPhraseVocabCard } from '../speaking/phraseCards';

const skillLabels: Record<CardType, string> = {
  speaking: '口语',
  writing: '写作',
  listening: '听力',
  vocab: '词汇',
};

const ratingLabels: Record<string, { text: string; color: string }> = {
  again: { text: '重来', color: 'var(--accent-red)' },
  hard: { text: '困难', color: 'var(--accent-yellow)' },
  good: { text: '掌握', color: 'var(--accent-green)' },
};

interface HistoryDetailState {
  loading: boolean;
  card?: CardRecord;
  entry?: CardHistoryEntry;
  error?: string;
}

export default function HistoryDetailPage() {
  const { cardId, entryIndex } = useParams<{ cardId: string; entryIndex: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<HistoryDetailState>({ loading: true });

  useEffect(() => {
    if (!cardId || entryIndex === undefined) {
      setState({ loading: false, error: '参数缺失' });
      return;
    }

    (async () => {
      const card = await defaultDb.cards.get(cardId);
      if (!card) {
        setState({ loading: false, error: '卡片不存在' });
        return;
      }

      const idx = parseInt(entryIndex, 10);
      const entry = card.history[idx];
      if (!entry) {
        setState({ loading: false, error: '历史记录不存在' });
        return;
      }

      setState({ loading: false, card, entry });
    })();
  }, [cardId, entryIndex]);

  if (state.loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"><span></span></div>
      </div>
    );
  }

  if (state.error || !state.card || !state.entry) {
    return (
      <div className="stack stack-lg">
        <div className="card">
          <div className="card-body">
            <p>{state.error || '加载失败'}</p>
            <button className="btn btn-secondary" onClick={() => navigate('/history')}>
              返回历史
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { card, entry } = state;
  const rating = ratingLabels[entry.rating] ?? { text: entry.rating, color: 'var(--text-muted)' };
  const answerMeta = entry.answerMeta as Record<string, unknown> | undefined;

  return (
    <div className="stack stack-lg" style={{ paddingBottom: '80px' }}>
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 0',
        }}
      >
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/history')}
          style={{ padding: '8px 12px' }}
        >
          ← 返回
        </button>
        <div>
          <div
            style={{
              fontSize: '11px',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              letterSpacing: '0.5px',
            }}
          >
            历史回顾
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {new Date(entry.ts).toLocaleString('zh-CN')}
          </div>
        </div>
      </header>

      {/* 历史标识 Banner */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '2px dashed var(--border-color)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <span style={{ fontSize: '20px' }}>📜</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px' }}>只读模式</div>
          <div className="caption" style={{ color: 'var(--text-muted)' }}>
            这是历史记录的回顾，无法修改
          </div>
        </div>
      </div>

      {/* 基本信息 */}
      <div className="card">
        <div className="card-body stack">
          <div className="flex items-center gap-sm">
            <span className={`skill-dot ${card.type}`} />
            <span style={{ fontWeight: 600 }}>{skillLabels[card.type]}</span>
            <span
              style={{
                fontSize: '11px',
                padding: '2px 8px',
                background: rating.color,
                color: '#000',
                fontWeight: 700,
              }}
            >
              {rating.text}
            </span>
          </div>

          <div className="caption" style={{ color: 'var(--text-muted)' }}>
            Mode: {entry.mode} · Interval: {entry.interval.toFixed(1)}d · Ease: {entry.ease.toFixed(2)}
          </div>

          {entry.notes && (
            <div className="caption" style={{ color: 'var(--text-secondary)' }}>
              Notes: {entry.notes}
            </div>
          )}
        </div>
      </div>

      {/* 卡片内容 */}
      <section className="section">
        <h2 className="section-title">卡片内容</h2>
        <CardContentDisplay card={card} />
      </section>

      {/* 答案记录 */}
      {answerMeta && Object.keys(answerMeta).length > 0 && (
        <section className="section">
          <h2 className="section-title">答案记录</h2>
          <AnswerMetaDisplay card={card} answerMeta={answerMeta} />
        </section>
      )}
    </div>
  );
}

function SpeakingContentDisplay({ card }: { card: CardRecord }) {
  const meta = card.speakingMeta;
  const content = card.content as { question?: string; topic?: string; cues?: string[] } | undefined;
  const extraMeta = meta?.extraMeta as Record<string, unknown> | undefined;
  const latestFeedback = extraMeta?.latestFeedback as {
    fluency?: string;
    grammar?: string;
    vocab?: string;
    betterVersion?: string;
  } | undefined;
  const thinkingHints = extraMeta?.thinkingHints as { phrases?: string[]; exampleAnswer?: string } | undefined;

  const question = content?.question || meta?.question;
  const topic = content?.topic || meta?.topic;
  const cues = content?.cues;
  const phrases = useMemo(
    () => (Array.isArray(thinkingHints?.phrases) ? thinkingHints?.phrases?.filter(Boolean) : []),
    [thinkingHints?.phrases]
  );
  const [phraseStatus, setPhraseStatus] = useState<Record<string, 'ready' | 'adding' | 'added' | 'failed'>>({});

  useEffect(() => {
    if (!phrases.length) return;
    let active = true;
    (async () => {
      const entries = await Promise.all(
        phrases.map(async (phrase) => {
          const id = buildSpeakingPhraseCardId(card, phrase);
          const existing = await defaultDb.cards.get(id);
          return [phrase, existing ? 'added' : 'ready'] as const;
        })
      );
      if (!active) return;
      setPhraseStatus((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      active = false;
    };
  }, [card, phrases]);

  const handleAddPhrase = async (phrase: string) => {
    setPhraseStatus((prev) => ({ ...prev, [phrase]: 'adding' }));
    try {
      await createSpeakingPhraseVocabCard(card, phrase, thinkingHints?.exampleAnswer);
      setPhraseStatus((prev) => ({ ...prev, [phrase]: 'added' }));
    } catch (err) {
      console.error('[history] add phrase failed', err);
      setPhraseStatus((prev) => ({ ...prev, [phrase]: 'failed' }));
    }
  };

  return (
    <div className="stack">
      {/* 题目信息 */}
      <div className="card">
        <div className="card-body stack">
          {topic && (
            <div>
              <div className="label">话题</div>
              <div>{topic}</div>
            </div>
          )}
          {question && (
            <div>
              <div className="label">问题</div>
              <div style={{ fontSize: '16px', fontWeight: 500 }}>{question}</div>
            </div>
          )}
          {cues && cues.length > 0 && (
            <div>
              <div className="label">提示要点</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {cues.map((cue, i) => (
                  <li key={i}>{cue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* 短语积累 */}
      {phrases.length > 0 && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">短语积累</div>
            <div className="caption" style={{ color: 'var(--text-muted)' }}>
              点击方块即可加入词汇练习
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {phrases.map((phrase) => {
                const status = phraseStatus[phrase] ?? 'ready';
                const isDisabled = status === 'adding' || status === 'added';
                const tone =
                  status === 'added'
                    ? { bg: 'rgba(0, 255, 136, 0.2)', border: 'var(--accent-green)', color: 'var(--accent-green)' }
                    : status === 'failed'
                      ? { bg: 'rgba(255, 0, 80, 0.12)', border: 'var(--accent-red)', color: 'var(--accent-red)' }
                      : { bg: 'var(--bg-secondary)', border: 'var(--border-color)', color: 'var(--text-primary)' };
                return (
                  <button
                    key={phrase}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => handleAddPhrase(phrase)}
                    style={{
                      padding: '10px 12px',
                      minWidth: '140px',
                      border: `2px solid ${tone.border}`,
                      background: tone.bg,
                      color: tone.color,
                      fontSize: '13px',
                      fontWeight: 600,
                      letterSpacing: '0.2px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                    title={status === 'added' ? '已加入词汇卡' : '点击加入词汇卡'}
                  >
                    {phrase}
                  </button>
                );
              })}
            </div>
            {Object.values(phraseStatus).includes('failed') && (
              <div className="caption" style={{ color: 'var(--accent-red)' }}>
                有短语添加失败，请重试
              </div>
            )}
          </div>
        </div>
      )}

      {/* 你的草稿/想法 */}
      {meta?.prompt && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">你的草稿 / 想法</div>
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '12px',
              border: '1px solid var(--border-color)',
              whiteSpace: 'pre-wrap',
              fontStyle: 'italic',
            }}>
              {meta.prompt}
            </div>
          </div>
        </div>
      )}

      {/* 录音回放 */}
      {(meta?.finalRecordingKey || meta?.draftRecordingKey) && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">录音回放</div>
            {meta?.finalRecordingKey && (
              <div>
                <div className="caption" style={{ marginBottom: '4px', color: 'var(--accent-green)' }}>
                  ✓ 正式录音
                </div>
                <audio controls src={meta.finalRecordingKey} style={{ width: '100%' }} />
              </div>
            )}
            {meta?.draftRecordingKey && meta.draftRecordingKey !== meta.finalRecordingKey && (
              <div>
                <div className="caption" style={{ marginBottom: '4px', color: 'var(--text-muted)' }}>
                  草稿录音
                </div>
                <audio controls src={meta.draftRecordingKey} style={{ width: '100%' }} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI 转写结果 */}
      {meta?.transcript && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">AI 转写</div>
            <div style={{
              background: 'var(--bg-secondary)',
              padding: '12px',
              border: '1px solid var(--border-color)',
              whiteSpace: 'pre-wrap',
            }}>
              {meta.transcript}
            </div>
          </div>
        </div>
      )}

      {/* AI 评估反馈 */}
      {latestFeedback && (latestFeedback.fluency || latestFeedback.grammar || latestFeedback.vocab) && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">AI 评估</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {latestFeedback.fluency && (
                <div style={{
                  flex: '1 1 auto',
                  minWidth: '120px',
                  padding: '8px 12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)'
                }}>
                  <div className="caption" style={{ color: 'var(--text-muted)' }}>流利度</div>
                  <div style={{ fontSize: '13px' }}>{latestFeedback.fluency}</div>
                </div>
              )}
              {latestFeedback.grammar && (
                <div style={{
                  flex: '1 1 auto',
                  minWidth: '120px',
                  padding: '8px 12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)'
                }}>
                  <div className="caption" style={{ color: 'var(--text-muted)' }}>语法</div>
                  <div style={{ fontSize: '13px' }}>{latestFeedback.grammar}</div>
                </div>
              )}
              {latestFeedback.vocab && (
                <div style={{
                  flex: '1 1 auto',
                  minWidth: '120px',
                  padding: '8px 12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)'
                }}>
                  <div className="caption" style={{ color: 'var(--text-muted)' }}>词汇</div>
                  <div style={{ fontSize: '13px' }}>{latestFeedback.vocab}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 改进版本 / 最佳答案 */}
      {(latestFeedback?.betterVersion || meta?.bestAnswerSummary) && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">改进版本</div>
            <div style={{
              background: 'rgba(0, 255, 136, 0.1)',
              padding: '12px',
              border: '1px solid var(--accent-green)',
              whiteSpace: 'pre-wrap',
              color: 'var(--accent-green)',
            }}>
              {latestFeedback?.betterVersion || meta?.bestAnswerSummary}
            </div>
          </div>
        </div>
      )}

      {/* 自评 */}
      {meta?.selfReview && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">自我评价</div>
            <div style={{ color: 'var(--text-secondary)' }}>{meta.selfReview}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardContentDisplay({ card }: { card: CardRecord }) {
  switch (card.type) {
    case 'speaking':
      return <SpeakingContentDisplay card={card} />;

    case 'writing':
      // 话题默写卡片
      if (card.mode === 'topic-dictation') {
        return <TopicDictationContentDisplay card={card} />;
      }
      // 其他写作卡片（段落回忆等）
      const writingContent = card.content as { prompt?: string; reference?: string } | undefined;
      return (
        <div className="card">
          <div className="card-body stack">
            {card.writingMeta?.cnHint && (
              <div>
                <div className="label">中文提示</div>
                <div style={{ fontSize: '16px' }}>{card.writingMeta.cnHint}</div>
              </div>
            )}
            {card.writingMeta?.enOriginal && (
              <div>
                <div className="label">参考答案</div>
                <div style={{ color: 'var(--accent-green)' }}>{card.writingMeta.enOriginal}</div>
                {card.writingMeta?.cnTranslation && (
                  <div style={{ color: 'var(--text-muted)', marginTop: '8px', fontSize: '14px' }}>
                    {card.writingMeta.cnTranslation}
                  </div>
                )}
              </div>
            )}
            {writingContent?.reference && (
              <div>
                <div className="label">参考</div>
                <div>{writingContent.reference}</div>
              </div>
            )}
          </div>
        </div>
      );

    case 'listening':
      return (
        <div className="card">
          <div className="card-body stack">
            {card.listeningMeta?.transcript && (
              <div>
                <div className="label">原文</div>
                <div style={{ fontSize: '16px' }}>{card.listeningMeta.transcript}</div>
              </div>
            )}
            {card.listeningMeta?.clozeTemplate && (
              <div>
                <div className="label">填空模板</div>
                <div style={{ fontFamily: 'monospace' }}>{card.listeningMeta.clozeTemplate}</div>
              </div>
            )}
          </div>
        </div>
      );

    case 'vocab':
      return (
        <div className="card">
          <div className="card-body stack">
            <div>
              <div className="label">英文</div>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>
                {card.vocabMeta?.en || card.vocabMeta?.front}
              </div>
            </div>
            <div>
              <div className="label">中文</div>
              <div style={{ fontSize: '16px' }}>{card.vocabMeta?.cn || card.vocabMeta?.back}</div>
            </div>
            {card.vocabMeta?.scene && (
              <div>
                <div className="label">场景</div>
                <div className="caption">{card.vocabMeta.scene}</div>
              </div>
            )}
            {card.vocabMeta?.examples && card.vocabMeta.examples.length > 0 && (
              <div>
                <div className="label">例句</div>
                {card.vocabMeta.examples.map((ex, i) => (
                  <div key={i} className="caption" style={{ marginTop: '4px' }}>
                    • {ex}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );

    default:
      return (
        <div className="card">
          <div className="card-body">
            <pre style={{ fontSize: '12px', overflow: 'auto' }}>
              {JSON.stringify(card.content, null, 2)}
            </pre>
          </div>
        </div>
      );
  }
}

/** 话题默写卡片内容展示 */
function TopicDictationContentDisplay({ card }: { card: CardRecord }) {
  const content = card.content as {
    majorTopic?: string;
    subTopic?: string;
    sentences?: Array<{ index: number; english: string; chinese: string }>;
  } | undefined;

  if (!content) {
    return (
      <div className="card">
        <div className="card-body">
          <p style={{ color: 'var(--text-muted)' }}>无内容</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      {/* 话题信息 */}
      <div className="card">
        <div className="card-body stack">
          {content.majorTopic && (
            <div>
              <div className="label">主题</div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>{content.majorTopic}</div>
            </div>
          )}
          {content.subTopic && (
            <div>
              <div className="label">子主题</div>
              <div style={{ fontSize: '14px' }}>{content.subTopic}</div>
            </div>
          )}
          {content.sentences && (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              共 {content.sentences.length} 句
            </div>
          )}
        </div>
      </div>

      {/* 句子列表 */}
      {content.sentences && content.sentences.length > 0 && (
        <div className="card">
          <div className="card-body stack">
            <div className="label">句库内容</div>
            {content.sentences.map((sentence) => (
              <div
                key={sentence.index}
                style={{
                  padding: '10px 12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  #{sentence.index}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {sentence.chinese}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--accent-green)' }}>
                  {sentence.english}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnswerMetaDisplay({ card, answerMeta }: { card: CardRecord; answerMeta: Record<string, unknown> }) {
  // 听力结果
  const listeningResult = answerMeta.listeningResult as ListeningResult | undefined;
  if (listeningResult) {
    if (listeningResult.mode === 'sentence') {
      const result = listeningResult as ListeningSentenceResult;
      return (
        <div className="card">
          <div className="card-body stack">
            <div className="flex justify-between items-center">
              <div className="label">准确率</div>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: result.accuracy >= 80 ? 'var(--accent-green)' : result.accuracy >= 50 ? 'var(--accent-yellow)' : 'var(--accent-red)',
                }}
              >
                {result.accuracy}%
              </div>
            </div>
            <div>
              <div className="label">原文</div>
              <div>{result.transcript}</div>
            </div>
            <div>
              <div className="label">你的答案</div>
              <div style={{ color: 'var(--text-secondary)' }}>{result.userAnswer}</div>
            </div>
            {result.summary.missing.length > 0 && (
              <div>
                <div className="label">漏掉的词</div>
                <div style={{ color: 'var(--accent-red)' }}>{result.summary.missing.join(', ')}</div>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      const result = listeningResult as ListeningClozeResult;
      return (
        <div className="card">
          <div className="card-body stack">
            <div className="flex justify-between items-center">
              <div className="label">填空准确率</div>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: result.accuracy >= 80 ? 'var(--accent-green)' : 'var(--accent-yellow)',
                }}
              >
                {result.accuracy}%
              </div>
            </div>
            {result.blanks.map((blank, i) => (
              <div key={i} className="flex gap-sm items-center">
                <span style={{ color: blank.correct ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {blank.correct ? '✓' : '✗'}
                </span>
                <span>期望: {blank.expected}</span>
                <span style={{ color: 'var(--text-muted)' }}>你写: {blank.answer}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  // 写作段落回忆
  const paragraphScore = answerMeta.paragraphRecallLocalScore as LocalParagraphScore | undefined;
  const paragraphAnswer = answerMeta.paragraphRecallAnswer as string | undefined;
  const paragraphLLM = answerMeta.paragraphRecallLLMFeedback as ParagraphLLMFeedback | undefined;

  if (paragraphScore) {
    return (
      <div className="card">
        <div className="card-body stack">
          <div className="flex justify-between items-center">
            <div className="label">覆盖率</div>
            <div
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: paragraphScore.coverage >= 80 ? 'var(--accent-green)' : 'var(--accent-yellow)',
              }}
            >
              {paragraphScore.coverage}%
            </div>
          </div>
          {paragraphAnswer && (
            <div>
              <div className="label">你的答案</div>
              <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{paragraphAnswer}</div>
            </div>
          )}
          {paragraphScore.missingUnits.length > 0 && (
            <div>
              <div className="label">缺失的关键短语</div>
              <div style={{ color: 'var(--accent-red)' }}>{paragraphScore.missingUnits.join(', ')}</div>
            </div>
          )}
          {paragraphLLM?.suggestions && paragraphLLM.suggestions.length > 0 && (
            <div>
              <div className="label">AI 建议</div>
              {paragraphLLM.suggestions.map((s, i) => (
                <div key={i} className="caption" style={{ marginTop: '4px' }}>
                  • {s}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 写作填空
  const writingCloze = answerMeta.writingClozeResult as { accuracy: number; blanks?: { expected: string; answer: string; correct: boolean }[] } | undefined;
  if (writingCloze) {
    return (
      <div className="card">
        <div className="card-body stack">
          <div className="flex justify-between items-center">
            <div className="label">填空准确率</div>
            <div
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: writingCloze.accuracy >= 80 ? 'var(--accent-green)' : 'var(--accent-yellow)',
              }}
            >
              {writingCloze.accuracy}%
            </div>
          </div>
          {writingCloze.blanks?.map((blank, i) => (
            <div key={i} className="flex gap-sm items-center">
              <span style={{ color: blank.correct ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                {blank.correct ? '✓' : '✗'}
              </span>
              <span>期望: {blank.expected}</span>
              <span style={{ color: 'var(--text-muted)' }}>你写: {blank.answer}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 话题默写
  const topicDictation = answerMeta.topicDictationResult as TopicDictationResult | undefined;
  if (topicDictation) {
    return <TopicDictationResultDisplay result={topicDictation} />;
  }

  // 通用显示
  return (
    <div className="card">
      <div className="card-body">
        <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '300px' }}>
          {JSON.stringify(answerMeta, null, 2)}
        </pre>
      </div>
    </div>
  );
}

// ============= 话题默写结果展示组件 =============

function TopicDictationResultDisplay({ result }: { result: TopicDictationResult }) {
  const { averageCoverage, sentenceScores, recallRatio } = result;
  const coverageColor = averageCoverage >= 80 ? 'var(--accent-green)' : averageCoverage >= 50 ? 'var(--accent-yellow)' : 'var(--accent-red)';

  return (
    <div className="stack">
      {/* 总览卡片 */}
      <div className="card">
        <div className="card-body stack">
          <div className="flex justify-between items-center">
            <div className="label">总覆盖率</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: coverageColor }}>
              {averageCoverage}%
            </div>
          </div>
          {/* 进度条 */}
          <div style={{
            height: '8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${averageCoverage}%`,
              background: coverageColor,
            }} />
          </div>
          <div className="flex justify-between" style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            <span>自评想起: {Math.round(recallRatio * 100)}%</span>
            <span>{sentenceScores.length} 句</span>
          </div>
        </div>
      </div>

      {/* 逐句结果 */}
      <div className="card">
        <div className="card-body stack">
          <div className="label">逐句对比</div>
          {sentenceScores.map((score) => (
            <SentenceResultItem key={score.index} score={score} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SentenceResultItem({ score }: { score: SentenceScore }) {
  const [expanded, setExpanded] = useState(false);
  const status = score.coverage >= 80 ? 'good' : score.coverage >= 50 ? 'partial' : 'weak';
  const statusColor = status === 'good' ? 'var(--accent-green)' : status === 'partial' ? 'var(--accent-yellow)' : 'var(--accent-red)';

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      background: 'var(--bg-secondary)',
    }}>
      {/* 标题行 - 可点击展开 */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: 'var(--text-muted)',
          minWidth: '24px',
        }}>
          #{score.index}
        </span>
        <span style={{
          flex: 1,
          fontSize: '13px',
          color: 'var(--text-secondary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {score.chinese}
        </span>
        <span style={{
          fontSize: '12px',
          fontWeight: 700,
          color: statusColor,
          minWidth: '40px',
          textAlign: 'right',
        }}>
          {score.coverage}%
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
          {expanded ? '▼' : '▶'}
        </span>
      </button>

      {/* 展开内容 */}
      {expanded && (
        <div style={{
          padding: '12px',
          borderTop: '1px solid var(--border-color)',
        }}>
          {/* Diff 对比 */}
          <div style={{ marginBottom: '12px' }}>
            <div className="label" style={{ marginBottom: '6px' }}>Diff 对比</div>
            <div style={{
              background: 'var(--bg-primary)',
              padding: '10px',
              border: '1px solid var(--border-color)',
              lineHeight: 1.8,
            }}>
              {renderDiffRanges(score.diffRanges)}
            </div>
          </div>

          {/* 用户答案 */}
          {score.answer && (
            <div>
              <div className="label" style={{ marginBottom: '6px' }}>你的答案</div>
              <div style={{
                background: 'var(--bg-primary)',
                padding: '10px',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
                fontSize: '13px',
              }}>
                {score.answer || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>（未作答）</span>}
              </div>
            </div>
          )}

          {/* 原文参考 */}
          <div style={{ marginTop: '12px' }}>
            <div className="label" style={{ marginBottom: '6px' }}>参考原文</div>
            <div style={{
              background: 'rgba(0, 255, 136, 0.05)',
              padding: '10px',
              border: '1px solid var(--accent-green)',
              color: 'var(--accent-green)',
              fontSize: '13px',
            }}>
              {score.english}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderDiffRanges(ranges: DiffRange[]) {
  return ranges.map((range, index) => {
    const key = `diff-${index}`;
    const baseStyle: React.CSSProperties = {
      display: 'inline',
      padding: '2px 4px',
      margin: '0 2px',
      fontSize: '13px',
    };

    switch (range.kind) {
      case 'hit':
        return (
          <span key={key} style={{
            ...baseStyle,
            background: 'rgba(0, 255, 136, 0.15)',
            color: 'var(--accent-green)',
          }}>
            {range.text}
          </span>
        );
      case 'typo':
        return (
          <span key={key} style={{
            ...baseStyle,
            background: 'rgba(255, 200, 0, 0.15)',
            color: 'var(--accent-yellow)',
            borderBottom: '2px dashed var(--accent-yellow)',
          }} title={`拼写错误: ${range.actual}`}>
            {range.text}
          </span>
        );
      case 'missing':
        return (
          <span key={key} style={{
            ...baseStyle,
            background: 'rgba(255, 0, 80, 0.15)',
            color: 'var(--accent-red)',
            textDecoration: 'line-through',
          }}>
            {range.text}
          </span>
        );
      case 'extra':
        return (
          <span key={key} style={{
            ...baseStyle,
            background: 'rgba(255, 0, 80, 0.1)',
            color: 'var(--accent-red)',
            fontStyle: 'italic',
            opacity: 0.7,
          }}>
            +{range.text}
          </span>
        );
      default:
        return <span key={key} style={baseStyle}>{range.text}</span>;
    }
  });
}
